import { z } from "zod";

export const photoCategorySchema = z.enum([
	"before",
	"after",
	"xray",
	"lab",
	"document",
	"general",
]);
export type PhotoCategory = z.infer<typeof photoCategorySchema>;

export const photoStatusSchema = z.enum(["pending", "ready", "failed"]);
export type PhotoStatus = z.infer<typeof photoStatusSchema>;

export const patientPhotoSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	skin_lesion_id: z.string().uuid().nullable(),
	uploaded_by: z.string().uuid().nullable(),
	r2_key: z.string(),
	mime_type: z.string(),
	size_bytes: z.number().int().nonnegative().nullable(),
	width: z.number().int().nonnegative().nullable(),
	height: z.number().int().nonnegative().nullable(),
	category: photoCategorySchema,
	caption: z.string().nullable(),
	taken_at: z.string().datetime().nullable(),
	status: photoStatusSchema,
	is_archived: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type PatientPhotoApi = z.infer<typeof patientPhotoSchema>;

export const ALLOWED_PHOTO_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/heic",
	"image/heif",
] as const;

export const MAX_PHOTO_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

export const requestPhotoUploadInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	skin_lesion_id: z.string().uuid().optional(),
	category: photoCategorySchema.default("general"),
	caption: z.string().max(500).optional(),
	mime_type: z.enum(ALLOWED_PHOTO_MIME_TYPES),
	size_bytes: z.number().int().positive().max(MAX_PHOTO_SIZE_BYTES),
});
export type RequestPhotoUploadInput = z.infer<
	typeof requestPhotoUploadInputSchema
>;

export const confirmPhotoUploadInputSchema = z.object({
	width: z.number().int().positive().optional(),
	height: z.number().int().positive().optional(),
});
export type ConfirmPhotoUploadInput = z.infer<
	typeof confirmPhotoUploadInputSchema
>;

export const updatePhotoInputSchema = z.object({
	category: photoCategorySchema.optional(),
	caption: z.string().max(500).nullable().optional(),
	taken_at: z.string().datetime().nullable().optional(),
	is_archived: z.boolean().optional(),
});
export type UpdatePhotoInput = z.infer<typeof updatePhotoInputSchema>;

export interface PresignedUploadResponse {
	photo_id: string;
	upload_url: string;
	upload_method: "PUT";
	headers: Record<string, string>;
	expires_in_seconds: number;
	r2_key: string;
}

export interface PresignedViewResponse {
	view_url: string;
	expires_in_seconds: number;
}
